/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

import kotlinx.datetime.LocalDate
import models.GoalAccountType
import models.GoalCategory
import models.SavingsGoal
import kotlin.math.max

/**
 * Assembles [ProjectionInputs] for each applicable vehicle and returns the
 * full set of [VehicleProjection]s — the data the Vehicle Comparison Strip
 * (§5.1) renders.
 *
 * Pure (no I/O). Platform UIs call this on every slider drag to get a fresh
 * comparison; the math engine lives entirely in shared.
 */
object StudioOrchestrator {

    /** v1 ships TFSA / RRSP / FHSA / HISA. RESP/RDSP/Non-registered are
     *  Phase 2. HISA is included as an educational "no growth" reference
     *  so the user can see what their money does *not* do when left as
     *  cash. */
    private val PHASE_1_VEHICLES = listOf(VehicleType.TFSA, VehicleType.RRSP, VehicleType.FHSA, VehicleType.HISA)

    /** Slider state the user can adjust at runtime. */
    data class SliderState(
        val monthlyContribution: Double,
        val expectedAnnualReturn: Double = 0.05,
        val monthsToGoal: Int,
        /** Future marginal rate at withdrawal. Default per spec §5.3 slider 4:
         *  `current - 0.05`, floored at 15%. The caller can override. */
        val futureMarginalRate: Double? = null,
    )

    /**
     * Default slider state derived from a goal + a profile. Today's date is
     * supplied so tests can pin to a known calendar; the platform UI uses
     * Clock.System.todayIn(...).
     */
    fun defaultSliders(goal: SavingsGoal, profile: Wiz3rUserProfile, today: LocalDate): SliderState {
        val months = max(1, monthsBetween(today, goal.deadline))
        // Match the formula the goal card itself displays — (target − current)
        // / monthsRemaining — so when the user opens the Studio the values
        // start exactly where they left off on the goal screen.
        val remaining = (goal.targetAmount - goal.currentAmount).coerceAtLeast(0.0)
        return SliderState(
            monthlyContribution = remaining / months,
            expectedAnnualReturn = 0.05,
            monthsToGoal = months,
            futureMarginalRate = (profile.tax.marginalRateNow - 0.05).coerceAtLeast(0.15),
        )
    }

    /**
     * Compute the per-vehicle projections plus the recommendation ranking.
     * Returns the projections in display order (BEST_FIT first when ties
     * exist, ordered by flexibility per spec §5.1 tie-breaker).
     */
    fun runComparison(
        goal: SavingsGoal,
        profile: Wiz3rUserProfile,
        sliders: SliderState,
        today: LocalDate,
    ): List<VehicleProjection> {
        val room = roomFromProfile(profile)
        val futureMarginal = sliders.futureMarginalRate
            ?: (profile.tax.marginalRateNow - 0.05).coerceAtLeast(0.15)
        val applicable = applicableVehicles(profile, goal)
        val projections = applicable.map { vehicle ->
            VehicleProjector.project(
                ProjectionInputs(
                    vehicle = vehicle,
                    monthlyContribution = sliders.monthlyContribution,
                    monthsToGoal = sliders.monthsToGoal,
                    expectedAnnualReturn = sliders.expectedAnnualReturn,
                    taxContext = profile.tax,
                    contributionRoom = room,
                    futureMarginalRate = futureMarginal,
                    today = today,
                ),
            )
        }
        return rankRecommendations(projections)
    }

    /**
     * Filter the Phase 1 vehicle set to those applicable for the goal +
     * profile. The projector enforces this too (returns `eligible = false`),
     * but pre-filtering keeps the comparison strip honest by not rendering
     * cards the user could never use.
     */
    fun applicableVehicles(profile: Wiz3rUserProfile, goal: SavingsGoal): List<VehicleType> {
        val byCategory = GoalAccountType.optionsFor(goal.category).toSet()
        return PHASE_1_VEHICLES.filter { v ->
            // Category filter — RRSP isn't appropriate for an emergency fund.
            v in byCategory &&
                // FHSA only when the user is a first-time home buyer.
                (v != VehicleType.FHSA || profile.tax.isFirstTimeHomeBuyer)
        }
    }

    /**
     * Build [ContributionRoom] from the user's profile answers. When the
     * user said UNKNOWN, we default to 0 (under-promise) so the engine never
     * recommends a contribution that exceeds reality.
     */
    fun roomFromProfile(profile: Wiz3rUserProfile): ContributionRoom = ContributionRoom(
        tfsa = if (profile.tfsaRoomKnowledge == RoomKnowledge.UNKNOWN) 0.0
        else profile.tfsaRoomAmount ?: 0.0,
        rrsp = if (profile.rrspRoomKnowledge == RoomKnowledge.UNKNOWN) 0.0
        else profile.rrspRoomAmount ?: 0.0,
        fhsa = FhsaRoom(
            remaining = 40_000.0,  // legislatively fixed lifetime cap; PR4 will
            // refine with the user's already-contributed amount if disclosed.
            lifetimeCap = 40_000.0,
            yearsUntilExpiry = 15,
        ),
        tfsaUnknown = profile.tfsaRoomKnowledge == RoomKnowledge.UNKNOWN,
        rrspUnknown = profile.rrspRoomKnowledge == RoomKnowledge.UNKNOWN,
    )

    /**
     * Apply the §5.1 ranking: highest `finalNetToUser` wins (BEST_FIT). Any
     * ineligible vehicle keeps its `INELIGIBLE` recommendation. Within 1% of
     * the best, the flexibility tie-breaker decides — TFSA > FHSA > RRSP.
     */
    fun rankRecommendations(projections: List<VehicleProjection>): List<VehicleProjection> {
        val (eligible, ineligible) = projections.partition { it.eligible }
        if (eligible.isEmpty()) return ineligible
        val bestNet = eligible.maxOf { it.finalNetToUser }
        val withinOnePercent = eligible.filter {
            bestNet > 0 && (bestNet - it.finalNetToUser) / bestNet <= 0.01
        }
        val winner = if (withinOnePercent.size > 1) {
            withinOnePercent.minByOrNull { flexibilityRank(it.vehicle) } ?: eligible.first()
        } else {
            eligible.maxByOrNull { it.finalNetToUser } ?: eligible.first()
        }
        val tagged = eligible.map { p ->
            val rec = when {
                p.vehicle == winner.vehicle -> Recommendation.BEST_FIT
                bestNet > 0 && p.finalNetToUser / bestNet < 0.90 -> Recommendation.NOT_IDEAL
                else -> Recommendation.BACKUP
            }
            p.copy(recommendation = rec)
        }
        // Stable display order: sort by flexibility rank (TFSA, FHSA, RRSP, …)
        // regardless of the current winner. Reordering on every slider drag
        // whipsaws the user's eye; the green "Best fit" badge alone signals
        // which one wins so the cards don't need to physically move.
        val ordered = tagged.sortedBy { flexibilityRank(it.vehicle) }
        return ordered + ineligible.sortedBy { flexibilityRank(it.vehicle) }
    }

    /**
     * Default account type per goal category. Used at goal creation so the
     * user doesn't face a blank dropdown — the orthodox choice is filled
     * in for them, and they can adjust via the Studio "Compare" CTA.
     */
    fun defaultAccountFor(category: GoalCategory, profile: Wiz3rUserProfile): GoalAccountType? = when (category) {
        GoalCategory.EMERGENCY_FUND -> GoalAccountType.HISA
        GoalCategory.VACATION -> GoalAccountType.TFSA
        GoalCategory.HOME_PURCHASE ->
            if (profile.tax.isFirstTimeHomeBuyer) GoalAccountType.FHSA else GoalAccountType.TFSA
        GoalCategory.RETIREMENT -> GoalAccountType.RRSP
        GoalCategory.EDUCATION -> GoalAccountType.RESP
        GoalCategory.MAJOR_PURCHASE -> GoalAccountType.TFSA
        GoalCategory.INVESTMENT -> GoalAccountType.TFSA
        GoalCategory.DEBT_PAYOFF -> null  // no account applies
        GoalCategory.OTHER -> null        // let user pick
    }

    /** Lower number = more flexible. Used to break ties at the top of the strip. */
    private fun flexibilityRank(vehicle: VehicleType): Int = when (vehicle) {
        VehicleType.HISA -> 0      // most flexible — cash, no rules
        VehicleType.TFSA -> 1
        VehicleType.FHSA -> 2
        VehicleType.RRSP -> 3
        VehicleType.NON_REGISTERED -> 4
        VehicleType.RESP -> 5
        VehicleType.RDSP -> 6
        else -> 9
    }

    // Months between two dates, from the one definition — analytics.goalMonthsBetween.
    // This used to inline the CALENDAR-BOUNDARY formula, deliberately, to line the
    // Studio's default up with what the goal card showed. The card was the one that
    // was wrong (it counted a part-month as whole and had the same goal costing $411/mo
    // against iOS's $417), so copying it propagated the error into shared code where
    // every platform picked it up. Agreeing with a wrong number is not consistency.
    private fun monthsBetween(from: LocalDate, to: LocalDate): Int =
        analytics.goalMonthsBetween(from, to)
}
